// ---------------------------------------------------------------------------
// DSS-15: 9 CSS filter sliders — blur, brightness, contrast, hue, etc.
// ---------------------------------------------------------------------------
import { useState, useCallback } from "react";
import { SlidersHorizontal } from "lucide-react";
import FilterSliderRow from "./filter-slider-row";

// ── Types ──
export interface FilterConfig {
  readonly blur: number;
  readonly brightness: number;
  readonly contrast: number;
  readonly hueRotate: number;
  readonly saturate: number;
  readonly grayscale: number;
  readonly invert: number;
  readonly sepia: number;
  readonly opacity: number;
  readonly blendMode: string;
  readonly state: "normal" | "hover";
}

// ── Constants ──
const DEFAULT_FILTER: FilterConfig = {
  blur: 0, brightness: 100, contrast: 100, hueRotate: 0,
  saturate: 100, grayscale: 0, invert: 0, sepia: 0, opacity: 100,
  blendMode: "normal",
  state: "normal",
};

interface FilterDef {
  readonly key: keyof Omit<FilterConfig, "state" | "blendMode">;
  readonly label: string;
  readonly min: number;
  readonly max: number;
  readonly unit: string;
  readonly defaultValue: number;
}

const FILTER_DEFS: readonly FilterDef[] = [
  { key: "blur", label: "Blur", min: 0, max: 50, unit: "px", defaultValue: 0 },
  { key: "brightness", label: "Brightness", min: 0, max: 200, unit: "%", defaultValue: 100 },
  { key: "contrast", label: "Contrast", min: 0, max: 200, unit: "%", defaultValue: 100 },
  { key: "hueRotate", label: "Hue Rotate", min: 0, max: 360, unit: "deg", defaultValue: 0 },
  { key: "saturate", label: "Saturate", min: 0, max: 200, unit: "%", defaultValue: 100 },
  { key: "grayscale", label: "Grayscale", min: 0, max: 100, unit: "%", defaultValue: 0 },
  { key: "invert", label: "Invert", min: 0, max: 100, unit: "%", defaultValue: 0 },
  { key: "sepia", label: "Sepia", min: 0, max: 100, unit: "%", defaultValue: 0 },
  { key: "opacity", label: "Opacity", min: 0, max: 100, unit: "%", defaultValue: 100 },
];

// ── CSS generation ──
export function generateFilterCss(c: FilterConfig): string {
  const parts: string[] = [];

  if (c.blur !== 0) parts.push(`blur(${c.blur}px)`);
  if (c.brightness !== 100) parts.push(`brightness(${c.brightness}%)`);
  if (c.contrast !== 100) parts.push(`contrast(${c.contrast}%)`);
  if (c.hueRotate !== 0) parts.push(`hue-rotate(${c.hueRotate}deg)`);
  if (c.saturate !== 100) parts.push(`saturate(${c.saturate}%)`);
  if (c.grayscale !== 0) parts.push(`grayscale(${c.grayscale}%)`);
  if (c.invert !== 0) parts.push(`invert(${c.invert}%)`);
  if (c.sepia !== 0) parts.push(`sepia(${c.sepia}%)`);
  if (c.opacity !== 100) parts.push(`opacity(${c.opacity}%)`);

  const filterRule = parts.length > 0 ? `filter: ${parts.join(" ")};` : "filter: none;";
  const blendRule = c.blendMode && c.blendMode !== "normal" ? ` mix-blend-mode: ${c.blendMode};` : "";
  return `${filterRule}${blendRule}`;
}

// ── Props ──
interface FilterControlsProps {
  readonly value: FilterConfig;
  readonly onChange: (config: FilterConfig) => void;
}

// ── Component ──
export default function FilterControls({ value, onChange }: FilterControlsProps) {
  const [activeState, setActiveState] = useState<"normal" | "hover">(value.state);

  const update = useCallback(
    (key: keyof Omit<FilterConfig, "state" | "blendMode">, v: number) => {
      onChange({ ...value, [key]: v });
    },
    [value, onChange],
  );

  const handleResetAll = useCallback(() => {
    onChange({ ...DEFAULT_FILTER, state: activeState });
  }, [onChange, activeState]);

  const handleStateTab = useCallback((s: "normal" | "hover") => {
    setActiveState(s);
    onChange({ ...value, state: s });
  }, [value, onChange]);

  const isAllDefault = FILTER_DEFS.every((d) => value[d.key] === d.defaultValue);

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <SlidersHorizontal className="h-3.5 w-3.5" />
        CSS Filters
      </div>

      {/* State tabs */}
      <div className="flex rounded-md border border-[var(--color-border)] p-0.5">
        {(["normal", "hover"] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => handleStateTab(s)}
            className={[
              "flex-1 rounded-sm px-3 py-1 text-[10px] font-medium capitalize",
              activeState === s
                ? "bg-[var(--color-primary)] text-white"
                : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
            ].join(" ")}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Sliders */}
      <div className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
        {FILTER_DEFS.map((def) => (
          <FilterSliderRow
            key={def.key}
            label={def.label}
            value={value[def.key]}
            defaultValue={def.defaultValue}
            min={def.min}
            max={def.max}
            unit={def.unit}
            onChange={(v) => update(def.key, v)}
          />
        ))}

        {/* Reset All */}
        <button
          type="button"
          onClick={handleResetAll}
          disabled={isAllDefault}
          className={[
            "mt-1 self-end rounded-md border px-3 py-1 text-[10px] font-medium",
            isAllDefault
              ? "border-[var(--color-border)] text-[var(--color-muted-foreground)]/40 cursor-default"
              : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]",
          ].join(" ")}
        >
          Reset All
        </button>
      </div>

      {/* CSS output */}
      <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
        <code className="whitespace-pre-wrap text-[10px] text-[var(--color-muted-foreground)]">
          {generateFilterCss(value)}
        </code>
      </div>
    </div>
  );
}
